import { Component, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AuthService } from '../../services/auth/auth.service';
import { NgClass, NgIf } from "@angular/common";
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NgIf, NgClass],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  loginForm: FormGroup;
  loginError = signal<string | null>(null);
  loginSuccess = signal<boolean>(false);
  loading = signal<boolean>(false);
  showPassword = false;
  failedAttempts = signal<number>(0);
  lockoutUntil = signal<Date | null>(null);

  constructor(
    private authService: AuthService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.loginForm = this.fb.group({
      login: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(4)]],
      rememberMe: [false]
    });

    // Auto-fill email if remembered
    const rememberedEmail = localStorage.getItem('rememberEmail');
    if (rememberedEmail) {
      this.loginForm.patchValue({ login: rememberedEmail, rememberMe: true });
    }
  }

  onSubmit(): void {
    if (this.loginForm.invalid) return;

    const now = new Date();
    if (this.lockoutUntil() && now < this.lockoutUntil()!) {
      const remaining = Math.ceil((this.lockoutUntil()!.getTime() - now.getTime()) / 60000);
      this.loginError.set(`Trop de tentatives échouées. Réessayez dans ${remaining} minute(s).`);
      return;
    }

    this.loading.set(true);
    const { login, password, rememberMe } = this.loginForm.value;

    this.authService.login(login, password).pipe(first()).subscribe({
      next: (response) => {
        this.loginSuccess.set(true);
        this.loginError.set(null);
        this.failedAttempts.set(0); // reset on success
        this.lockoutUntil.set(null);

        // Handle remember me
        if (rememberMe) {
          localStorage.setItem('rememberEmail', login);
        } else {
          localStorage.removeItem('rememberEmail');
        }

        switch (response?.data?.platform) {
          case 'BACKOFFICE_ADMIN':
            this.router.navigate(['/admin']);
            break;
          case 'BACKOFFICE_COMPANY':
            this.router.navigate(['/company']);
            break;
          default:
            this.loginError.set('Type de plateforme inconnu.');
            break;
        }
      },
      error: (error) => {
        console.error(error);
        this.loginSuccess.set(false);
        this.loginError.set('Login échoué, veuillez réessayer.');
        this.failedAttempts.set(this.failedAttempts() + 1);
        if (this.failedAttempts() >= 5) {
          this.lockoutUntil.set(new Date(now.getTime() + 5 * 60 * 1000)); // 5 minutes
        }
        this.loading.set(false);
      },
      complete: () => {
        this.loading.set(false);
      }
    });
  }

  googlePassword(): void {
    this.showPassword = !this.showPassword;
  }

  get isLockedOut(): boolean {
    const lockout = this.lockoutUntil();
    if (!lockout) return false;
    const now = new Date();
    return now < lockout;
  }
}
